import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { trpc } from '../lib/trpc';
import type { Region, CityLocation } from '../components/SearchPage/RegionSearch';
import {
  readSearchScope,
  writeSearchScope,
  boundsToCenterAndRadius,
} from '../utils/searchScope';

export type MapBounds = {
  ne: { lat: number; lng: number };
  sw: { lat: number; lng: number };
};

export type LocationParam = {
  lat: number;
  lng: number;
  radius: number; // meters
};

export type ScopePill = {
  label: string;
  onDismiss: () => void;
} | null;

export type GeoPermission = 'prompt' | 'granted' | 'denied';

export interface UseSearchScopeReturn {
  // State (read)
  selectedRegion: Region | null;
  selectedCity: CityLocation | null;
  userLocation: { lat: number; lng: number } | null;
  mapBounds: MapBounds | null;
  pendingMapBounds: MapBounds | null;
  geoPermission: GeoPermission;
  isLocating: boolean;
  showGeoPrompt: boolean;
  geoError: string | null;
  scopeRestored: boolean;

  // Derived
  locationParam: LocationParam | undefined;
  boundsParam: MapBounds | undefined;
  hasLocation: boolean;
  isMapAreaMode: boolean;
  hasUncommittedPan: boolean;
  scopePill: ScopePill;
  initialMapBounds: { north: number; south: number; east: number; west: number } | undefined;
  mapCenter: { lat: number; lng: number } | undefined;
  mapZoom: number | undefined;

  // Handlers
  handleRegionChange: (region: Region | null) => void;
  handleCitySearch: (city: CityLocation) => void;
  handleUseMyLocation: (onSuccess?: () => void) => void;
  handleMapBoundsChanged: (bounds: MapBounds) => void;
  handleCommitPendingBounds: () => void;
  handleClearMapArea: () => void;
  setShowGeoPrompt: (show: boolean) => void;
}

/**
 * Maps a browser GeolocationPositionError to a user-facing message for the
 * "Use my current location" flow.
 */
const geoErrorMessage = (err: GeolocationPositionError): string => {
  switch (err.code) {
    case err.PERMISSION_DENIED:
      return 'Location access was denied. Enable it in your browser settings to search nearby.';
    case err.POSITION_UNAVAILABLE:
      return "We couldn't determine your location. Check your device's location settings and try again.";
    case err.TIMEOUT:
      return 'Getting your location took too long. Please try again.';
    default:
      return "We couldn't get your location. Please try again.";
  }
};

/**
 * Architecture: the URL params are the single source of truth for
 * `region`, `city`, and `mapBounds`. These three are exposed as memoized
 * derivations of `useSearchParams()` — there is no `useState` mirror, no
 * URL→state effect, and no state→URL effect.
 *
 * Handlers (`handleRegionChange`, `handleCitySearch`, `handleCommitPendingBounds`,
 * etc.) write the URL directly via `updateScopeUrl`. The derived values
 * recompute on the next render automatically. No bidirectional sync to race.
 *
 * `userLocation` and `pendingMapBounds` are NOT URL-backed and remain as
 * `useState`:
 *   - `userLocation` comes from the browser geolocation API; the URL doesn't
 *     carry it because it's volatile and per-session.
 *   - `pendingMapBounds` tracks live map viewport changes BEFORE the user
 *     commits via "Search this area". A `useEffect` keeps it synced with
 *     `mapBounds` (the committed, URL-backed value) so reloads honor the
 *     URL viewport.
 */
export const useSearchScope = (): UseSearchScopeReturn => {
  const [searchParams, setSearchParams] = useSearchParams();

  // --- Parse URL scope params ---
  const urlRegion = searchParams.get('region') || '';

  const urlBoundsN = searchParams.get('n');
  const urlBoundsS = searchParams.get('s');
  const urlBoundsE = searchParams.get('e');
  const urlBoundsW = searchParams.get('w');
  const urlBounds = useMemo<MapBounds | null>(() => {
    if (urlBoundsN && urlBoundsS && urlBoundsE && urlBoundsW) {
      return {
        ne: { lat: parseFloat(urlBoundsN), lng: parseFloat(urlBoundsE) },
        sw: { lat: parseFloat(urlBoundsS), lng: parseFloat(urlBoundsW) },
      };
    }
    return null;
  }, [urlBoundsN, urlBoundsS, urlBoundsE, urlBoundsW]);

  const urlCity = searchParams.get('city') || '';
  const urlCityLat = searchParams.get('cityLat') || '';
  const urlCityLng = searchParams.get('cityLng') || '';
  const urlCityObj = useMemo<CityLocation | null>(() => {
    if (urlCity && urlCityLat && urlCityLng) {
      return {
        name: urlCity,
        lat: parseFloat(urlCityLat),
        lng: parseFloat(urlCityLng),
      };
    }
    return null;
  }, [urlCity, urlCityLat, urlCityLng]);

  // --- Local state (NOT URL-backed) ---
  const [userLocation, setUserLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [pendingMapBounds, setPendingMapBounds] = useState<MapBounds | null>(urlBounds);
  const [isLocating, setIsLocating] = useState(false);
  const [geoPermission, setGeoPermission] = useState<GeoPermission>('prompt');
  const [geoError, setGeoError] = useState<string | null>(null);
  const [showGeoPrompt, setShowGeoPrompt] = useState(false);
  const [scopeRestored, setScopeRestored] = useState(false);

  // Implicit baseline for pan-divergence detection when no location anchor
  // (region/city/userLocation) exists. Captures the first non-null
  // pendingMapBounds and never updates after, so any subsequent pan reads
  // as "diverged" — surfaces "Search this area" on the first pan after
  // a fresh, unscoped page load.
  const firstPendingBoundsRef = useRef<MapBounds | null>(null);

  // --- Regions list (needed to resolve URL slug to Region object) ---
  const { data: allRegionsRaw } = trpc.region.list.useQuery();
  const allRegions = allRegionsRaw as Region[] | undefined;

  // --- Derived: URL-backed scope state ---
  // These are pure memoized derivations of the URL. No setters, no effects.
  const selectedRegion = useMemo<Region | null>(() => {
    if (!urlRegion || !allRegions) return null;
    return allRegions.find((r) => r.slug === urlRegion) ?? null;
  }, [urlRegion, allRegions]);

  const selectedCity = urlCityObj;
  const mapBounds = urlBounds;

  // --- Sync pendingMapBounds to mapBounds when the URL viewport changes ---
  // Without this, navigating to a page with `?n=&s=&e=&w=` would leave
  // pendingMapBounds stuck at its initial value and "Search this area"
  // would fire immediately on first map render. We only re-sync when
  // mapBounds (URL) actually changes — the user's live pan via
  // handleMapBoundsChanged sets pendingMapBounds independently, and this
  // effect doesn't interfere (mapBounds is unchanged during a pan).
  useEffect(() => {
    setPendingMapBounds(mapBounds);
  }, [mapBounds]);

  // --- URL update helper ---
  // Single entry point for writing scope to the URL. All handlers funnel
  // through here. Pages can keep their own page-only URL writes (filters,
  // sort, page) via their own helpers — those use different keys and don't
  // collide with the scope keys this hook manages.
  const updateScopeUrl = useCallback(
    (overrides: Record<string, string | null>) => {
      const next = new URLSearchParams(searchParams);
      for (const [k, v] of Object.entries(overrides)) {
        if (v === null) next.delete(k);
        else next.set(k, v);
      }
      setSearchParams(next, { replace: true });
      // Any deliberate scope change dismisses a stale geolocation error.
      setGeoError(null);
    },
    [searchParams, setSearchParams]
  );

  // --- Request geolocation on mount ---
  const geoRequested = useRef(false);
  useEffect(() => {
    if (geoRequested.current) return;
    geoRequested.current = true;

    if (sessionStorage.getItem('wx.geoDismissed') === '1') {
      setGeoPermission('denied');
      return;
    }

    if (!navigator.geolocation) {
      setGeoPermission('denied');
      return;
    }

    if (navigator.permissions) {
      navigator.permissions.query({ name: 'geolocation' }).then((result) => {
        setGeoPermission(result.state as GeoPermission);
        if (result.state === 'granted') {
          navigator.geolocation.getCurrentPosition(
            (pos) => {
              setUserLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude });
              setGeoPermission('granted');
            },
            (err) => {
              // Permission is already 'granted' here — a timeout or
              // position-unavailable must not downgrade it to 'denied'.
              if (err.code === err.PERMISSION_DENIED) setGeoPermission('denied');
            },
            { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 }
          );
        } else if (result.state === 'prompt') {
          setShowGeoPrompt(true);
        }
      });
    } else {
      setShowGeoPrompt(true);
    }
  }, []);

  // --- Cross-page sessionStorage restore (one-shot, after regions load) ---
  // If the URL has no scope params, pull the most recent region/city/
  // userLocation/area set on any search page in this session and apply it.
  // URL params always win over sessionStorage. After restore (or after
  // determining there's nothing to restore), `scopeRestored` flips true
  // and the persist effect can safely run.
  useEffect(() => {
    if (scopeRestored) return;
    if (!allRegions) return;

    const hasUrlScope = !!urlRegion || !!urlCity || !!urlBounds;
    if (!hasUrlScope) {
      const scope = readSearchScope();
      if (scope) {
        if (scope.region) {
          const match = allRegions.find((r) => r.slug === scope.region!.slug);
          if (match) {
            updateScopeUrl({ region: match.slug });
          }
        } else if (scope.city) {
          updateScopeUrl({
            city: scope.city.name,
            cityLat: String(scope.city.lat),
            cityLng: String(scope.city.lng),
          });
        } else if (scope.userLocation) {
          setUserLocation(scope.userLocation);
        } else if (scope.area) {
          // Bounds-derived area persisted by a map-having page (Wineries).
          // Restore as n/s/e/w URL params via inverse of boundsToCenterAndRadius.
          const lat = scope.area.lat;
          const lng = scope.area.lng;
          const radiusMeters = scope.area.radiusMeters;
          const dLat = radiusMeters / 111_000;
          const dLng = radiusMeters / (111_000 * Math.cos((lat * Math.PI) / 180));
          updateScopeUrl({
            n: String(lat + dLat),
            s: String(lat - dLat),
            e: String(lng + dLng),
            w: String(lng - dLng),
          });
        }
      }
    }
    setScopeRestored(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allRegions, scopeRestored]);

  // --- Persist active scope to sessionStorage ---
  // Priority mirrors locationParam: bounds > region > city > userLocation.
  // Gated on `scopeRestored` so the initial all-null render doesn't blow
  // away an incoming scope.
  useEffect(() => {
    if (!scopeRestored) return;
    if (mapBounds) {
      writeSearchScope({ area: boundsToCenterAndRadius(mapBounds) });
    } else if (selectedRegion) {
      writeSearchScope({ region: { slug: selectedRegion.slug } });
    } else if (selectedCity) {
      writeSearchScope({
        city: {
          name: selectedCity.name,
          lat: selectedCity.lat,
          lng: selectedCity.lng,
        },
      });
    } else if (userLocation) {
      writeSearchScope({ userLocation: { lat: userLocation.lat, lng: userLocation.lng } });
    } else {
      writeSearchScope(null);
    }
  }, [mapBounds, selectedRegion, selectedCity, userLocation, scopeRestored]);

  // --- Determine if any location anchor is active ---
  const hasLocation = !!selectedRegion || !!selectedCity || !!userLocation;
  const isMapAreaMode = !!mapBounds;

  // --- Derived: locationParam ---
  const locationParam = useMemo((): LocationParam | undefined => {
    if (mapBounds) {
      return {
        lat: (mapBounds.ne.lat + mapBounds.sw.lat) / 2,
        lng: (mapBounds.ne.lng + mapBounds.sw.lng) / 2,
        radius: 50000,
      };
    }
    if (selectedRegion) {
      const [lng, lat] = selectedRegion.coordinates.coordinates;
      return { lat, lng, radius: selectedRegion.defaultRadius * 1000 };
    }
    if (selectedCity) {
      return { lat: selectedCity.lat, lng: selectedCity.lng, radius: 25_000 };
    }
    if (userLocation) {
      return { lat: userLocation.lat, lng: userLocation.lng, radius: 50000 };
    }
    return undefined;
  }, [mapBounds, selectedRegion, selectedCity, userLocation]);

  const boundsParam = useMemo(() => {
    return mapBounds || undefined;
  }, [mapBounds]);

  const initialMapBounds = useMemo(() => {
    const loc = locationParam;
    if (!loc) return undefined;
    const latDelta = loc.radius / 111000;
    const lngDelta = loc.radius / (111000 * Math.cos((loc.lat * Math.PI) / 180));
    return {
      north: loc.lat + latDelta,
      south: loc.lat - latDelta,
      east: loc.lng + lngDelta,
      west: loc.lng - lngDelta,
    };
  }, [locationParam]);

  const hasUncommittedPan = useMemo(() => {
    if (!pendingMapBounds) return false;
    const initialPending = firstPendingBoundsRef.current;
    const baseline = mapBounds
      ? {
          north: mapBounds.ne.lat,
          south: mapBounds.sw.lat,
          east: mapBounds.ne.lng,
          west: mapBounds.sw.lng,
        }
      : initialMapBounds ??
        (initialPending
          ? {
              north: initialPending.ne.lat,
              south: initialPending.sw.lat,
              east: initialPending.ne.lng,
              west: initialPending.sw.lng,
            }
          : undefined);
    if (!baseline) return false;
    const baseCenterLat = (baseline.north + baseline.south) / 2;
    const baseCenterLng = (baseline.east + baseline.west) / 2;
    const baseDiagonal = Math.sqrt(
      Math.pow(baseline.north - baseline.south, 2) + Math.pow(baseline.east - baseline.west, 2)
    );
    const pendingCenterLat = (pendingMapBounds.ne.lat + pendingMapBounds.sw.lat) / 2;
    const pendingCenterLng = (pendingMapBounds.ne.lng + pendingMapBounds.sw.lng) / 2;
    const pendingDiagonal = Math.sqrt(
      Math.pow(pendingMapBounds.ne.lat - pendingMapBounds.sw.lat, 2) +
        Math.pow(pendingMapBounds.ne.lng - pendingMapBounds.sw.lng, 2)
    );
    const centerDist = Math.sqrt(
      Math.pow(baseCenterLat - pendingCenterLat, 2) +
        Math.pow(baseCenterLng - pendingCenterLng, 2)
    );
    const positionDiverged = centerDist > 0.1 * baseDiagonal;
    const zoomDiverged =
      pendingDiagonal < 0.75 * baseDiagonal || pendingDiagonal > 1.25 * baseDiagonal;
    return positionDiverged || zoomDiverged;
  }, [pendingMapBounds, mapBounds, initialMapBounds]);

  const mapCenter = useMemo(() => {
    if (mapBounds) {
      return {
        lat: (mapBounds.ne.lat + mapBounds.sw.lat) / 2,
        lng: (mapBounds.ne.lng + mapBounds.sw.lng) / 2,
      };
    }
    if (selectedRegion) {
      const [lng, lat] = selectedRegion.coordinates.coordinates;
      return { lat, lng };
    }
    if (selectedCity) return { lat: selectedCity.lat, lng: selectedCity.lng };
    if (userLocation) return userLocation;
    return undefined;
  }, [mapBounds, selectedRegion, selectedCity, userLocation]);

  const mapZoom = useMemo(() => {
    if (mapBounds) {
      const diag = Math.sqrt(
        Math.pow(mapBounds.ne.lat - mapBounds.sw.lat, 2) +
          Math.pow(mapBounds.ne.lng - mapBounds.sw.lng, 2)
      );
      return (
        Math.max(3, Math.min(16, Math.round((10 - Math.log2(Math.max(diag, 0.01))) * 10) / 10))
      );
    }
    if (selectedRegion) {
      return Math.round((14 - Math.log2(selectedRegion.defaultRadius)) * 10) / 10;
    }
    if (selectedCity) {
      return 11;
    }
    return undefined;
  }, [mapBounds, selectedRegion, selectedCity]);

  // --- Handlers — all write URL directly via updateScopeUrl ---

  /**
   * Sets the active region. Clears city, userLocation, and mapBounds.
   * Consumer pages should watch `selectedRegion` and reset their own
   * pagination/sort state in a `useEffect`.
   */
  const handleRegionChange = useCallback(
    (region: Region | null) => {
      updateScopeUrl({
        region: region?.slug ?? null,
        city: null,
        cityLat: null,
        cityLng: null,
        n: null,
        s: null,
        e: null,
        w: null,
      });
      setUserLocation(null);
      firstPendingBoundsRef.current = null;
    },
    [updateScopeUrl]
  );

  /**
   * Sets the active city. Clears region, userLocation, and mapBounds.
   * Consumer pages should watch `selectedCity` and reset pagination/sort.
   */
  const handleCitySearch = useCallback(
    (city: CityLocation) => {
      updateScopeUrl({
        region: null,
        city: city.name,
        cityLat: String(city.lat),
        cityLng: String(city.lng),
        n: null,
        s: null,
        e: null,
        w: null,
      });
      setUserLocation(null);
      firstPendingBoundsRef.current = null;
    },
    [updateScopeUrl]
  );

  const requestGeolocation = useCallback((onSuccess?: () => void) => {
    if (!navigator.geolocation) {
      setGeoPermission('denied');
      setGeoError('Your browser does not support location services.');
      return;
    }
    // Clear the session dismiss flag — explicit user action overrides the
    // earlier "no thanks" so future reloads can auto-resolve again.
    sessionStorage.removeItem('wx.geoDismissed');
    setGeoError(null);
    setIsLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setUserLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setGeoPermission('granted');
        setShowGeoPrompt(false);
        setIsLocating(false);
        onSuccess?.();
      },
      (err) => {
        // Only a real permission denial should latch 'denied'. A timeout or
        // position-unavailable must leave the user able to retry.
        if (err.code === err.PERMISSION_DENIED) setGeoPermission('denied');
        setGeoError(geoErrorMessage(err));
        setShowGeoPrompt(false);
        setIsLocating(false);
      },
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 }
    );
  }, []);

  /**
   * Triggers geolocation. On success, sets userLocation and clears region,
   * city, and mapBounds. The `onSuccess` callback fires after the URL
   * clear so consumer pages can reset pagination in the same tick.
   */
  const handleUseMyLocation = useCallback(
    (onSuccess?: () => void) => {
      requestGeolocation(() => {
        updateScopeUrl({
          region: null,
          city: null,
          cityLat: null,
          cityLng: null,
          n: null,
          s: null,
          e: null,
          w: null,
        });
        firstPendingBoundsRef.current = null;
        onSuccess?.();
      });
    },
    [requestGeolocation, updateScopeUrl]
  );

  const handleMapBoundsChanged = useCallback((bounds: MapBounds) => {
    if (!firstPendingBoundsRef.current) {
      firstPendingBoundsRef.current = bounds;
    }
    setPendingMapBounds(bounds);
  }, []);

  /**
   * Commits pendingMapBounds to the URL as n/s/e/w params. The mapBounds
   * derivation picks them up on the next render. Consumer pages should
   * watch `mapBounds` and reset pagination.
   */
  const handleCommitPendingBounds = useCallback(() => {
    if (!pendingMapBounds) return;
    updateScopeUrl({
      n: String(pendingMapBounds.ne.lat),
      s: String(pendingMapBounds.sw.lat),
      e: String(pendingMapBounds.ne.lng),
      w: String(pendingMapBounds.sw.lng),
    });
    firstPendingBoundsRef.current = null;
  }, [pendingMapBounds, updateScopeUrl]);

  /**
   * Clears the committed mapBounds (dismisses "Map area" mode). The
   * pendingMapBounds sync effect will follow when the URL change settles.
   */
  const handleClearMapArea = useCallback(() => {
    updateScopeUrl({ n: null, s: null, e: null, w: null });
    firstPendingBoundsRef.current = null;
  }, [updateScopeUrl]);

  // --- Active-scope pill ---
  // Surfaces what's filtering the results. Precedence mirrors `locationParam`
  // (bounds > region > city > geo). Dismiss handlers write URL/state directly.
  const scopePill = useMemo<ScopePill>(() => {
    if (mapBounds) {
      return {
        label: 'Searching in this area',
        onDismiss: handleClearMapArea,
      };
    }
    if (selectedRegion) {
      return {
        label: `Searching in ${selectedRegion.name}`,
        onDismiss: () => handleRegionChange(null),
      };
    }
    if (selectedCity) {
      return {
        label: `Searching near ${selectedCity.name}`,
        onDismiss: () =>
          updateScopeUrl({ city: null, cityLat: null, cityLng: null }),
      };
    }
    if (userLocation) {
      return {
        label: 'Searching near you',
        onDismiss: () => {
          sessionStorage.setItem('wx.geoDismissed', '1');
          setUserLocation(null);
        },
      };
    }
    return null;
  }, [
    mapBounds,
    selectedRegion,
    selectedCity,
    userLocation,
    handleClearMapArea,
    handleRegionChange,
    updateScopeUrl,
  ]);

  return {
    selectedRegion,
    selectedCity,
    userLocation,
    mapBounds,
    pendingMapBounds,
    geoPermission,
    isLocating,
    showGeoPrompt,
    geoError,
    scopeRestored,
    locationParam,
    boundsParam,
    hasLocation,
    isMapAreaMode,
    hasUncommittedPan,
    scopePill,
    initialMapBounds,
    mapCenter,
    mapZoom,
    handleRegionChange,
    handleCitySearch,
    handleUseMyLocation,
    handleMapBoundsChanged,
    handleCommitPendingBounds,
    handleClearMapArea,
    setShowGeoPrompt,
  };
};
